import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpacePhotoCommentInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpacePhotoCommentColumns } from './AdSpacePhotoCommentColumns';
import { AdSpacePhotoCommentsUpdateDialog } from './AdSpacePhotoCommentsUpdateDialog'; 

const AdSpacePhotoCommentsAdminList: React.FC = () => {
    const [comments, setComments] = useState<AdSpacePhotoCommentInterface[]>([]);
    const [comment, setComment] = useState<AdSpacePhotoCommentInterface | null>(null);
    const [commentId, setCommentId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<AdSpacePhotoCommentInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [commentToUpdate, setCommentToUpdate] = useState<AdSpacePhotoCommentInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all comments
    const handleGetAllComments = async () => {
        setError(null);
        setComment(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePhotoCommentInterface[]>>('/adSpacePhotoComments');
            if (result.status && result.data) {
                setComments(result.data);
            } else {
                setError(result.message || 'Failed to fetch comments');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch comments');
        }
    };

    // Fetch comment by id
    const handleGetCommentById = async () => {
        setError(null);
        setComments([]);
        setSuccess(null);
        if (!commentId) return setError('Please enter a Comment ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePhotoCommentInterface>>(`/adSpacePhotoComments/${commentId}`);
            if (result.status && result.data) {
                setComment(result.data);
            } else {
                setError(result.message || 'Failed to fetch comment');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch comment');
        }
    };

    // Delete comment
    const handleDeleteComment = async () => {
        if (!commentToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/adSpacePhotoComments/delete/${commentToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Comment deleted successfully');
                setDeleteDialogOpen(false);
                setCommentToDelete(null);
                handleGetAllComments();
            } else {
                setError(result.message || 'Failed to delete comment');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete comment');
        }
    };

    // Update comment
    const handleUpdateComment = async () => {
        if (!commentToUpdate || !commentToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<AdSpacePhotoCommentInterface>>(
                `/adSpacePhotoComments/update/${commentToUpdate.id}`,
                'PUT',
                commentToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Comment updated successfully');
                setUpdateDialogOpen(false);
                setCommentToUpdate(null);
                handleGetAllComments();
            } else {
                setUpdateError(result.message || 'Failed to update comment');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update comment');
        }
    };

    const columns = getAdSpacePhotoCommentColumns(
        setCommentToDelete,
        setDeleteDialogOpen,
        setCommentToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllComments();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllComments}>Get All Comments</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetCommentById}>Get Comment by ID</Button>
                <TextField
                    label="Comment ID"
                    value={commentId}
                    onChange={e => setCommentId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {comments.length > 0 && (
                    <DataGrid
                        rows={comments.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {comment && (
                    <DataGrid
                        rows={[{ ...comment, id: comment.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this comment?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteComment}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpacePhotoCommentsUpdateDialog
                open={updateDialogOpen}
                commentToUpdate={commentToUpdate}
                setCommentToUpdate={setCommentToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateComment}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpacePhotoCommentsAdminList;